import React, { useState, useEffect, useRef, useContext, useCallback } from "react";
import { useHistory, useParams } from "react-router-dom";
import { parseISO, format, isSameDay } from "date-fns";
import clsx from "clsx";

import {
    makeStyles,
    useTheme,
    ListItem,
    ListItemAvatar,
    Avatar,
    Badge,
    ListItemText,
    ListItemSecondaryAction,
    Typography,
    Tooltip
} from "@material-ui/core";

import { green, grey } from "@material-ui/core/colors";
import { i18n } from "../../translate/i18n";

import api from "../../services/api";
import ButtonWithSpinner from "../ButtonWithSpinner";
import MarkdownWrapper from "../MarkdownWrapper";
import { AuthContext } from "../../context/Auth/AuthContext";
import { TicketsContext } from "../../context/Tickets/TicketsContext";
import toastError from "../../errors/toastError";
import { v4 as uuidv4 } from "uuid";

import GroupIcon from "@material-ui/icons/Group";
import ContactTag from "../ContactTag";
import ConnectionIcon from "../ConnectionIcon";
import AcceptTicketWithouSelectQueue from "../AcceptTicketWithoutQueueModal";
import TransferTicketModalCustom from "../TransferTicketModalCustom";
import ShowTicketOpen from "../ShowTicketOpenModal";
import { isNil } from "lodash";
import { toast } from "react-toastify";

import {
    Done as AcceptIcon,
    HighlightOff as CloseIcon,
    Replay as ReopenIcon,
    SwapHoriz as TransferIcon,
    AccountBox,
    AttachFile as AttachFileIcon,
    InsertPhoto as InsertPhotoIcon,
    MovieCreation as MovieCreationIcon,
} from "@material-ui/icons";

// Ícones MUI v5 para estados de presença (digitando / gravando)
import CreateIcon from '@mui/icons-material/Create';
import MicIcon from '@mui/icons-material/Mic';

import useCompanySettings from "../../hooks/useSettings/companySettings";
import { Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button } from "@material-ui/core";
import Error from '@material-ui/icons/Error';
import VerifiedUser from '@material-ui/icons/VerifiedUser';
import { SocketContext } from "../../context/Socket/SocketContext";
import { socketManager } from "../../context/Socket/SocketContext";

// ----------------------------------------
// Constantes unificadas de presença WA
// ----------------------------------------
const WA_ACTIVE_SOFT_TTL = 4500; // ms janela normal de exibição de digitando/gravando
const WA_ACTIVE_SWEEP_INTERVAL = 500; // ms intervalo do sweep
const WA_ACTIVE_RENEW_THRESHOLD = 6000; // ms: se um novo composing chegar após esse gap não renova (considerado pulso fantasma)
// HARD CAP opcional (não aplicado diretamente – rely no ignore de renovação) poderia ser adicionado se necessário

// -----------------------------
// Cache Global de Presença WA
// -----------------------------
// Estrutura:
// waPresenceCache[remoteJid] = { lastUpdate, presence, participants: { [participantJid]: { presence, ts } }, aliases: Set<aliasKey> }
const waPresenceCache = {};
// Helper global para inspeção manual no console
try { if (!window.__waPresenceDump) window.__waPresenceDump = () => JSON.parse(JSON.stringify(waPresenceCache)); } catch {}

const FILE_PREFIX_REGEX = /^(\d{10,})([_-])(.*)$/;
const EXTENSION_CAPTURE_REGEX = /\.([a-z0-9]{1,6})$/i;
const IMAGE_FILE_EXTENSIONS = new Set(["jpg", "jpeg", "png", "gif", "bmp", "webp", "svg", "heic", "heif"]);
const VIDEO_FILE_EXTENSIONS = new Set(["mp4", "mov", "m4v", "avi", "mkv", "webm", "3gp", "3g2", "mpg", "mpeg"]);

const detectAttachmentSummary = (value) => {
    if (!value) return null;
    const sanitized = `${value}`.toLowerCase();
    const match = sanitized.match(EXTENSION_CAPTURE_REGEX);
    if (!match) return null;
    const ext = match[1];
    if (IMAGE_FILE_EXTENSIONS.has(ext)) {
        return { type: "image", label: "Imagem" };
    }
    if (VIDEO_FILE_EXTENSIONS.has(ext)) {
        return { type: "video", label: "Vídeo" };
    }
    return { type: "document", label: "Documento" };
};

const resolveRemoteJid = (ticket) => {
    if (!ticket) return null;
    if (ticket?.contact?.remoteJid) return ticket.contact.remoteJid;
    if (ticket?.isGroup) {
        if (ticket?.contact?.number) return `${ticket.contact.number}@g.us`;
    } else if (ticket?.contact?.number) {
        return `${ticket.contact.number}@s.whatsapp.net`;
    }
    return null;
};

// Recupera entrada do cache aceitando variações (jid completo, apenas dígitos, lower-case)
const getCacheEntry = (jid) => {
    if (!jid) return null;
    const lc = jid.toLowerCase();
    const digits = lc.replace(/[^0-9]/g, "");
    if (waPresenceCache[lc]) return waPresenceCache[lc];
    if (waPresenceCache[digits]) return waPresenceCache[digits];
    if (digits) {
        const foundKey = Object.keys(waPresenceCache).find(k => k.endsWith(digits) && waPresenceCache[k]);
        if (foundKey) return waPresenceCache[foundKey];
    }
    return null;
};

const getPresenceDisplay = (ticket) => {
    const jid = resolveRemoteJid(ticket);
    if (!jid) return null;
    const entry = getCacheEntry(jid);
    if (!entry) return null;
    const now = Date.now();
    const ACTIVE_TTL = WA_ACTIVE_SOFT_TTL; // usar constante unificada
    if (jid.endsWith('@g.us')) {
        const active = Object.entries(entry.participants || {})
            .filter(([, v]) => v && ["composing","recording"].includes(v.activeState || v.presence) && (now - (v.activeTs || v.ts || 0)) <= ACTIVE_TTL)
            .map(([k,v]) => ({ participant: k, presence: v.activeState || v.presence }));
        if (!active.length) return null;
        const recording = active.filter(a => a.presence === 'recording');
        const composing = active.filter(a => a.presence === 'composing');
        if (recording.length) return { type: 'recording', label: recording.length === 1 ? 'Gravando...' : `${recording.length} gravando...` };
        if (composing.length) return { type: 'composing', label: composing.length === 1 ? 'Digitando...' : `${composing.length} digitando...` };
        return null;
    }
    // Contato 1:1
    if (entry.activeState && (now - entry.activeTs) <= ACTIVE_TTL) {
        if (entry.activeState === 'composing') return { type: 'composing', label: 'Digitando...' };
        if (entry.activeState === 'recording') return { type: 'recording', label: 'Gravando...' };
    }
    // Não mostrar 'Online' como texto (mantido apenas na bolinha), então retorno null para baseline
    return null;
};

const isOnlinePresence = (ticket) => {
    const jid = resolveRemoteJid(ticket);
    if (!jid) return false;
    const entry = getCacheEntry(jid);
    if (!entry) return false;
    const now = Date.now();
    const ONLINE_WINDOW = 60000;
    if (jid.endsWith('@g.us')) {
        return Object.values(entry.participants || {}).some(v => {
            if (!v) return false;
            const baseOk = v.basePresence === 'available' && (now - (v.lastAvailableTs || v.ts || 0)) < ONLINE_WINDOW;
            const activeOk = v.activeState && (now - v.activeTs) <= 6000;
            return baseOk || activeOk;
        });
    }
    const baseOk = entry.basePresence === 'available' && (now - (entry.lastAvailableTs || entry.lastUpdate || 0)) < ONLINE_WINDOW;
    const activeOk = entry.activeState && (now - entry.activeTs) <= 6000;
    return baseOk || activeOk;
};

// ----------------------------------------
// Estilos principais
// ----------------------------------------
const useStyles = makeStyles((theme) => ({
    ticket: {
        position: "relative",
        borderRadius: 10,
        marginRight: "-80px",
        marginTop: 3,
        height: 90,
    },
    compactTicket: {
        height: 60,
        marginRight: -40,
    },
    selectedListItem: {
        "&.Mui-selected": {
            backgroundColor: theme.mode === "light" ? "#eeeeee" : grey[900],
        },
        "&.Mui-selected:hover": {
            backgroundColor: theme.mode === "light" ? "#e0e0e0" : grey[800],
        },
    },
    unreadTicket: {
        // Option B: destaque mais elegante com gradiente lateral
        background: theme.mode === 'light'
            ? 'linear-gradient(90deg, rgba(255,193,7,0.28) 0%, rgba(255,193,7,0.08) 55%, transparent 100%)'
            : 'linear-gradient(90deg, rgba(255,193,7,0.23) 0%, rgba(255,193,7,0.08) 50%, transparent 100%)',
        borderLeft: '4px solid #FFC107',
        position: 'relative',
    },
    pendingTicket: {
        cursor: "unset",
    },
    queueTag: {
        background: "#f8f8f8",
        color: "#000",
        marginRight: 1,
        padding: 1,
        fontWeight: "bold",
        paddingRight: 5,
        borderRadius: 3,
        fontSize: "0.5em",
        whiteSpace: "nowrap",
    },
    noTicketsDiv: {
        display: "flex",
        height: 100,
        margin: 40,
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
    },
    noTicketsText: {
        textAlign: "center",
        color: "rgb(104, 121, 146)",
        fontSize: 14,
        lineHeight: "1.4",
    },
    noTicketsTitle: {
        textAlign: "center",
        fontSize: 16,
        fontWeight: 600,
        margin: 0,
    },
    avatarContainer: {
        position: "relative",
        width: 60,
        height: 60,
        marginTop: -10,
    },
    compactAvatarContainer: {
        width: 40,
        height: 40,
        marginTop: -4,
    },
    avatar: {
        width: "100%",
        height: "100%",
        borderRadius: "50%",
    },
    badgeContainer: {
        position: "relative",
        top: -60,
        left: 55,
    },
    compactBadgeContainer: {
        top: -40,
        left: 38,
    },
    connectionIconContainer: {
        position: "absolute",
        bottom: -15,
        left: "50%",
        transform: "translateX(-50%)",
    },
    compactConnectionIconContainer: {
        bottom: -10,
    },
    badgeStyle: {
        color: "#FFF",
        backgroundColor: green[500],
    },
    contactNameWrapper: {
        display: "flex",
        justifyContent: "space-between",
        marginLeft: 10,
        fontWeight: "bold",
        color: theme.mode === "light" ? "black" : grey[200],
    },
    contactLastMessage: {
        paddingRight: 0,
        marginLeft: 5,
        margin: "0 8px 0 0",
        color: theme.mode === "light" ? "black" : grey[200],
    },
    contactLastMessageUnread: {
        paddingRight: 20,
        fontWeight: "bold",
        color: theme.mode === "light" ? "black" : grey[200],
        width: "80%",
    },
    actionButtonsColumn: {
        display: "flex",
        flexDirection: "column",
        gap: 1,
        position: "relative",
        marginRight: "-8px",
    },
    iconButtonBase: {
        width: 40,
        height: 25,
        borderRadius: 6,
        color: "#FFF",
        opacity: 0.7,
        transition: "opacity 0.5s ease-in-out",
        "&:hover": {
            opacity: 1,
            backgroundColor: "inherit !important",
        },
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        minWidth: "unset",
        boxShadow: "none",
        border: "none",
    },
    acceptButtonStyle: {
        backgroundColor: "#4CAF50",
        "&:hover": {
            backgroundColor: "#4CAF50 !important",
        },
    },
    transferButtonStyle: {
        backgroundColor: "#1976D2",
        "&:hover": {
            backgroundColor: "#1976D2 !important",
        },
    },
    closeButtonStyle: {
        backgroundColor: "#8a8a8a",
        "&:hover": {
            backgroundColor: "#D32F2F !important",
        },
    },
    ignoreButtonStyle: {
        backgroundColor: "#8a8a8a",
        "&:hover": {
            backgroundColor: "#ad0c14 !important",
        },
    },
    reopenButtonStyle: {
        backgroundColor: "#8a8a8a",
        "&:hover": {
            backgroundColor: "#5f5f5f !important",
        },
    },
    lastMessageTime: {
        justifySelf: 'flex-end',
        textAlign: 'right',
        fontSize: 13,
        marginLeft: 8,
        color: theme.mode === 'light' ? theme.palette.success.main : (theme.palette.error.main || '#FF5252'),
        fontWeight: 500,
        whiteSpace: 'nowrap'
    },
    secondaryContentSecond: {
        display: "flex",
        alignItems: "flex-start",
        flexWrap: "nowrap",
        flexDirection: "row",
        alignContent: "flex-start",
    },
    connectionIcon: {
        width: 18,
        height: 18,
    },
    connectionTag: {
        background: "green",
        color: "#FFF",
        marginRight: 3,
        padding: "2px 8px",
        borderRadius: 5,
        fontSize: "0.7em",
        whiteSpace: "nowrap",
    },
    timeChip: {
        background: theme.palette.primary.main,
        color: theme.palette.primary.contrastText,
        borderRadius: 14,
        padding: '2px 10px',
        fontSize: 14,
        fontWeight: 400,
        lineHeight: 1.2,
        display: 'inline-block',
        minWidth: 46,
        textAlign: 'center'
    },
    // Estilo do grupo de botões do modal replicado (igual ao da conversa)
    botoes: {
        padding: theme.spacing(2),
        gap: theme.spacing(2),
        justifyContent: "center",
    },
}));

// ----------------------------------------
// Componente Principal
// ----------------------------------------
const TicketListItemCustom = ({ setTabOpen, ticket, compact = false }) => {
    const classes = useStyles();
    const theme = useTheme();
    const history = useHistory();
    const { ticketId } = useParams();

    const [loading, setLoading] = useState(false);
    const [acceptTicketWithouSelectQueueOpen, setAcceptTicketWithouSelectQueueOpen] = useState(false);
    const [transferTicketModalOpen, setTransferTicketModalOpen] = useState(false);
    const [openAlert, setOpenAlert] = useState(false);
    const [userTicketOpen, setUserTicketOpen] = useState("");
    const [queueTicketOpen, setQueueTicketOpen] = useState("");
    const [confirmAskOpen, setConfirmAskOpen] = useState(false);
    const [manualTicketEnabled, setManualTicketEnabled] = useState(false);
    const confirmAskResolver = useRef(null);

    // Flag para saber se o componente ainda está montado
    const isMounted = useRef(true);
    const { setCurrentTicket, setTabOpen: setTabOpenCtx } = useContext(TicketsContext);
    const { user } = useContext(AuthContext);

    const { get: getSetting } = useCompanySettings();
    // Unificação de socket: utilizar apenas instância única (AuthContext deve prover em nível superior).
    // Mantemos fallback ao SocketContext legacy apenas para compatibilidade se necessário.
    const unifiedCtx = useContext(SocketContext);
    const socket = unifiedCtx?.GetSocket?.() || null;
    const [presenceTick, setPresenceTick] = useState(0); // força re-render quando presença muda

    useEffect(() => {
        return () => {
            isMounted.current = false;
        };
    }, []);

    useEffect(() => {
        let active = true;
        const loadManualFlag = async () => {
            try {
                const setting = await getSetting({ column: "manualTicketCreation" });
                if (!active) return;
                setManualTicketEnabled(String(setting?.manualTicketCreation || "disabled") === "enabled");
            } catch (err) {
                if (!active) return;
                setManualTicketEnabled(false);
            }
        };
        if (user?.companyId) {
            loadManualFlag();
        } else {
            setManualTicketEnabled(false);
        }
        return () => { active = false; };
    }, [getSetting, user?.companyId]);

    // Listener global de presença (unificado) com debounce e sincronização de campos legados
    const presenceListenerRef = useRef(null);
    const pendingRenderMapRef = useRef({});
    useEffect(() => {
        if (!socket || !user?.companyId) return;
        const eventName = `company-${user.companyId}-wa-presence`;
        if (presenceListenerRef.current) return; // já anexado globalmente
    const ACTIVE_STATES = new Set(['composing','recording']); // 'paused' não mantém mais estado ativo
        const handler = (data) => {
            if (!data || data.action !== 'wa-presence') return;
            let { remoteJid, participant, presence, ts } = data;
            if (!remoteJid) return;
            remoteJid = String(remoteJid).toLowerCase();
            const digits = remoteJid.replace(/[^0-9]/g, "");
            if (!waPresenceCache[remoteJid]) {
                waPresenceCache[remoteJid] = { lastUpdate: ts, presence: null, basePresence: null, lastAvailableTs: null, activeState: null, activeTs: null, participants: {}, aliases: new Set() };
            }
            const entry = waPresenceCache[remoteJid];
            if (digits && !waPresenceCache[digits]) waPresenceCache[digits] = entry;
            if (entry.aliases && digits) entry.aliases.add(digits);
            // Deduplicação available muito próxima do último available (janela 250ms)
            if (presence === 'available' && entry.lastAvailableTs && (ts - entry.lastAvailableTs) < 250) {
                // Ignora duplicado imediato
                return;
            }
            // Aplicar presença
            const applyActive = (target, p) => { target.activeState = p; target.activeTs = ts; };
            const clearActive = (target) => { target.activeState = null; target.activeTs = null; };
            const applyBase = (target, p) => {
                if (p === 'available') { target.basePresence = 'available'; target.lastAvailableTs = ts; }
                else if (p === 'unavailable') { target.basePresence = 'unavailable'; }
            };
            if (remoteJid.endsWith('@g.us')) {
                if (participant) {
                    if (!entry.participants[participant]) entry.participants[participant] = { basePresence: null, lastAvailableTs: null, activeState: null, activeTs: null };
                    const pt = entry.participants[participant];
                    if (presence === 'paused') { // limpar imediatamente
                        clearActive(pt);
                    } else if (ACTIVE_STATES.has(presence)) {
                        // Renovação condicionada: se gap > threshold, considerar pulso fantasma e ignorar
                        if (pt.activeState === presence && pt.activeTs) {
                            const gap = ts - pt.activeTs;
                            if (gap > WA_ACTIVE_RENEW_THRESHOLD) {
                                if (window.__WA_PRESENCE_DEBUG) { try { console.log('[WA-PRESENCE][IGNORE-PULSE][GROUP]', { remoteJid, participant, presence, gap }); } catch {} }
                                // Não renova timestamp -> deixará expirar
                            } else {
                                applyActive(pt, presence);
                            }
                        } else {
                            applyActive(pt, presence);
                        }
                    } else {
                        applyBase(pt, presence);
                        if (presence === 'unavailable') clearActive(pt);
                        // Se chegou available e activeState ficou 'stale' (>2s), limpar para mostrar só online
                        if (presence === 'available' && pt.activeState && (ts - pt.activeTs) > 2000) clearActive(pt);
                    }
                }
            } else {
                if (presence === 'paused') {
                    clearActive(entry);
                } else if (ACTIVE_STATES.has(presence)) {
                    if (entry.activeState === presence && entry.activeTs) {
                        const gap = ts - entry.activeTs;
                        if (gap > WA_ACTIVE_RENEW_THRESHOLD) {
                            if (window.__WA_PRESENCE_DEBUG) { try { console.log('[WA-PRESENCE][IGNORE-PULSE]', { remoteJid, presence, gap }); } catch {} }
                            // Ignora renovação tardia (pulso fantasma)
                        } else {
                            applyActive(entry, presence);
                        }
                    } else {
                        applyActive(entry, presence);
                    }
                } else {
                    applyBase(entry, presence);
                    if (presence === 'unavailable') clearActive(entry);
                    if (presence === 'available' && entry.activeState && (ts - entry.activeTs) > 2000) clearActive(entry);
                }
            }
            entry.lastUpdate = ts;
            // Sincronizar campo legado presence para renderizações existentes
            entry.presence = entry.activeState || entry.basePresence || null;
            if (window.__WA_PRESENCE_DEBUG) { try { console.log('[WA-PRESENCE][CACHE-ENTRY]', remoteJid, { presence: entry.presence, base: entry.basePresence, active: entry.activeState, lastAvail: entry.lastAvailableTs }); } catch {} }
            // Agendar re-render dos tickets que usam este JID ou alias
            const scheduleRender = (jidNorm) => {
                if (pendingRenderMapRef.current[jidNorm]) return; // já agendado
                pendingRenderMapRef.current[jidNorm] = true;
                setTimeout(() => {
                    delete pendingRenderMapRef.current[jidNorm];
                    // Forçar apenas um tick global; cada item checa se mudou relevante para si (simplificação: incrementa sempre)
                    setPresenceTick(t => t + 1);
                }, 50);
            };
            scheduleRender(remoteJid);
            if (digits) scheduleRender(digits);
        };
        socket.on(eventName, handler);
        presenceListenerRef.current = handler;
        return () => {
            socket.off(eventName, handler);
            presenceListenerRef.current = null;
        };
    }, [socket, user?.companyId]);

    // Expiração de estados voláteis (digitando / gravando) - varredura mais rápida (1s) + sincroniza presence
    useEffect(() => {
        const interval = setInterval(() => {
            const now = Date.now();
            let changed = false;
            Object.keys(waPresenceCache).forEach(jid => {
                const entry = waPresenceCache[jid];
                if (!entry) return;
                const ACTIVE_TTL = WA_ACTIVE_SOFT_TTL; // usar constante unificada
                if (jid.endsWith('@g.us')) {
                    Object.keys(entry.participants).forEach(p => {
                        const pt = entry.participants[p];
                        if (!pt) return;
                        if (pt.activeState && (now - pt.activeTs) > ACTIVE_TTL) {
                            pt.activeState = null;
                            pt.activeTs = null;
                            changed = true;
                        }
                        // Atualiza presence derivado
                        pt.presence = pt.activeState || pt.basePresence || null;
                    });
                } else {
                    if (entry.activeState && (now - entry.activeTs) > ACTIVE_TTL) {
                        entry.activeState = null;
                        entry.activeTs = null;
                        changed = true;
                    }
                    entry.presence = entry.activeState || entry.basePresence || null;
                }
            });
            if (changed) {
                setPresenceTick(t => t + 1);
            }
    }, WA_ACTIVE_SWEEP_INTERVAL); // sweep configurável
        if (window.__WA_PRESENCE_DEBUG) { try { console.log('[WA-PRESENCE][FRONT] listener ativo', { hasSocket: !!socket, ns: socket?.nsp, keys: Object.keys(waPresenceCache).length }); } catch {} }
        return () => clearInterval(interval);
    }, [socket]);

    // Permite que setTabOpen seja opcional via prop; usa contexto como fallback
    const setTabOpenSafe = useCallback(
        (tab) => {
            const fn = setTabOpen || setTabOpenCtx;
            if (typeof fn === "function") fn(tab);
        },
        [setTabOpen, setTabOpenCtx]
    );

    const handleOpenAcceptTicketWithouSelectQueue = useCallback(() => {
        if (isMounted.current) {
            setAcceptTicketWithouSelectQueueOpen(true);
        }
    }, []);

    const handleCloseAlert = useCallback(() => {
        if (isMounted.current) {
            setOpenAlert(false);
            setLoading(false);
        }
    }, []);

    // Diálogo para modo "ask": retorna true/false, e undefined se fechar sem escolher
    const askShouldSendFarewell = () => new Promise((resolve) => {
        setConfirmAskOpen(true);
        confirmAskResolver.current = (val) => {
            setConfirmAskOpen(false);
            resolve(val);
        };
    });

    // Fechamento do modal por clique fora/ESC: cancela ação (resolve undefined)
    const handleAskDialogClose = () => {
        const resolver = confirmAskResolver.current;
        confirmAskResolver.current = null;
        setConfirmAskOpen(false);
        if (typeof resolver === 'function') {
            resolver(undefined);
        }
    };

    // Encerrar Ticket (Atendendo): respeita closePendingListFarewellMode (always/ask/never)
    const handleCloseTicket = async (id) => {
        // Determina se envia despedida
        let sendFarewell = true;
        try {
            const modeSetting = await getSetting({ column: "closePendingListFarewellMode" });
            const mode = (modeSetting?.closePendingListFarewellMode || "always").toLowerCase();
            if (mode === "never") sendFarewell = false;
            if (mode === "ask") {
                const answer = await askShouldSendFarewell();
                if (answer === undefined) {
                    // Usuário fechou o modal sem escolher: cancela ação
                    return;
                }
                sendFarewell = answer;
            }
        } catch {}

        const setting = await getSetting({ column: "requiredTag" });

        if (setting.requiredTag === "enabled") {
            try {
                const contactTags = await api.get(`/contactTags/${ticket.contact.id}`);
                if (!contactTags.data.tags) {
                    toast.warning(i18n.t("messagesList.header.buttons.requiredTag"));
                } else {
                    await api.put(`/tickets/${id}`, {
                        status: "closed",
                        userId: user?.id || null,
                        sendFarewellMessage: sendFarewell,
                    });
                    if (isMounted.current) {
                        setLoading(false);
                    }
                    history.push(`/tickets/`);
                }
            } catch (err) {
                if (isMounted.current) {
                    setLoading(false);
                }
                toastError(err);
            }
        } else {
            if (isMounted.current) {
                setLoading(true);
            }
            try {
                await api.put(`/tickets/${id}`, {
                    status: "closed",
                    userId: user?.id || null,
                    sendFarewellMessage: sendFarewell,
                });
            } catch (err) {
                if (isMounted.current) {
                    setLoading(false);
                }
                toastError(err);
                return;
            }
            if (isMounted.current) {
                setLoading(false);
            }
            history.push(`/tickets/`);
        }
    };

    // Fechar ticket SEM despedida explicitamente
    const handleCloseIgnoreTicket = async (id) => {
        if (isMounted.current) {
            setLoading(true);
        }
        try {
            await api.put(`/tickets/${id}`, {
                status: "closed",
                userId: user?.id || null,
                sendFarewellMessage: false,
                amountUsedBotQueues: 0,
            });
        } catch (err) {
            if (isMounted.current) {
                setLoading(false);
            }
            toastError(err);
            return;
        }
        if (isMounted.current) {
            setLoading(false);
        }
        history.push(`/tickets/`);
    };

    // Fechar pendente respeitando configuração closePendingListFarewellMode
    const handleClosePendingTicket = async (id) => {
        try {
            const setting = await getSetting({ column: "closePendingListFarewellMode" });
            const mode = (setting?.closePendingListFarewellMode || "always").toLowerCase();

            let sendFarewell = true;
            if (mode === "never") {
                sendFarewell = false;
            } else if (mode === "ask") {
                // Abre diálogo e aguarda escolha
                const answer = await askShouldSendFarewell();
                if (answer === undefined) {
                    // Cancelado pelo usuário
                    return;
                }
                sendFarewell = answer;
            }

            if (isMounted.current) setLoading(true);
            await api.put(`/tickets/${id}`, {
                status: "closed",
                userId: user?.id || null,
                sendFarewellMessage: sendFarewell,
                amountUsedBotQueues: 0,
            });
        } catch (err) {
            if (isMounted.current) setLoading(false);
            toastError(err);
            return;
        }
        if (isMounted.current) setLoading(false);
        history.push(`/tickets/`);
    };

    // Modal de Transferência
    const handleCloseTransferTicketModal = useCallback(() => {
        if (isMounted.current) {
            setTransferTicketModalOpen(false);
        }
    }, []);

    const handleOpenTransferModal = (e) => {
        if (e) {
            e.stopPropagation();
            e.preventDefault();
        }
        if (isMounted.current) {
            setTransferTicketModalOpen(true);
        }
        // Não navegar nem selecionar o ticket aqui; manter na lista e apenas abrir o modal
    };

    // Aceitar Ticket
    const handleAcepptTicket = async (id) => {
        if (isMounted.current) {
            setLoading(true);
        }
        try {
            const otherTicket = await api.put(`/tickets/${id}`, {
                status: ticket.isGroup && ticket.channel === "whatsapp" ? "group" : "open",
                userId: user?.id,
            });

                if (otherTicket.data.id !== ticket.id) {
                if (otherTicket.data.userId !== user?.id) {
                    if (isMounted.current) {
                        setOpenAlert(true);
                        setUserTicketOpen(otherTicket.data.user.name);
                        setQueueTicketOpen(otherTicket.data.queue.name);
                    }
                } else {
                    if (isMounted.current) {
                        setLoading(false);
                            setTabOpenSafe(ticket.isGroup ? "group" : "open");
                    }
                    handleSelectTicket(otherTicket.data);
                    history.push(`/tickets/${otherTicket.uuid}`);
                }
            } else {
                let setting;
                try {
                    setting = await getSetting({ column: "sendGreetingAccepted" });
                } catch (err) {
                    toastError(err);
                }

                if (
                    setting?.sendGreetingAccepted === "enabled" &&
                    (!ticket.isGroup || ticket.whatsapp?.groupAsTicket === "enabled")
                ) {
                    // Saudação enviada pelo backend ao aceitar ticket
                }
                if (isMounted.current) {
                    setLoading(false);
                    setTabOpenSafe(ticket.isGroup ? "group" : "open");
                    handleSelectTicket(ticket);
                }
                history.push(`/tickets/${ticket.uuid}`);
            }
        } catch (err) {
            if (isMounted.current) {
                setLoading(false);
            }
            toastError(err);
        }
    };

    // Seleciona Ticket ao clicar
    const handleSelectTicket = (ticket) => {
        const code = uuidv4();
        const { id, uuid } = ticket;
        setCurrentTicket({ id, uuid, code });
    };

    const handleOpenSession = async (e) => {
        if (e && typeof e.stopPropagation === 'function') e.stopPropagation();
        if (isMounted.current) setLoading(true);
        try {
            const { data } = await api.get("/tickets", {
                params: {
                    searchParam: ticket?.contact?.number || ticket?.contact?.name,
                    status: "search",
                    showAll: true,
                },
            });

            const list = Array.isArray(data?.tickets) ? data.tickets : [];
            const candidates = list
                .filter((t) => t?.contact?.id === ticket?.contact?.id && t?.status !== "closed")
                .sort((a, b) => new Date(b.updatedAt).getTime() - new Date(a.updatedAt).getTime());

            const session = candidates.find((t) => !t.isOfficial);
            const official = candidates.find((t) => t.isOfficial);

            if (session) {
                if (session?.userId && session.userId !== user?.id) {
                    setUserTicketOpen(session?.user?.name || "Outro atendente");
                    setQueueTicketOpen(session?.queue?.name || "Fila");
                    setOpenAlert(true);
                } else {
                    setOpenAlert(false);
                    const tab = session.isGroup && session.channel === 'whatsapp' ? 'group' : 'open';
                    setTabOpenSafe(tab);
                    handleSelectTicket(session);
                    history.push('/tickets');
                    setTimeout(() => {
                        history.push(`/tickets/${session.uuid}`);
                    }, 10);
                }
                if (isMounted.current) setLoading(false);
                return;
            }

            if (official) {
                if (official?.userId && official.userId !== user?.id) {
                    setUserTicketOpen(official?.user?.name || "Outro atendente");
                    setQueueTicketOpen(official?.queue?.name || "Fila");
                    setOpenAlert(true);
                } else {
                    toast.warning("Há um ticket oficial aberto para este contato. Encerre o ticket oficial para abrir uma sessão.");
                }
                if (isMounted.current) setLoading(false);
                return;
            }
        } catch (searchErr) {
            // Continua para criação se busca falhar
        }

        const queueId = ticket.queueId || (Array.isArray(user?.queues) && user.queues.length === 1 ? user.queues[0].id : null);
        const whatsappId = user?.whatsappId || ticket?.whatsapp?.id;

        if (!queueId || !whatsappId) {
            if (isMounted.current) setLoading(false);
            toast.warning("Selecione uma fila e conexão para iniciar a sessão.");
            return;
        }

        try {
            const { data: newTicket } = await api.post("/tickets", {
                contactId: ticket.contact?.id,
                queueId,
                whatsappId,
                userId: user?.id,
                status: "open",
            });

            if (isMounted.current) setLoading(false);
            const tab = newTicket.isGroup && newTicket.channel === 'whatsapp' ? 'group' : 'open';
            setTabOpenSafe(tab);
            handleSelectTicket(newTicket);
            history.push('/tickets');
            setTimeout(() => {
                history.push(`/tickets/${newTicket.uuid}`);
            }, 10);
        } catch (err) {
            if (isMounted.current) setLoading(false);
            const errorData = err?.response?.data?.error;
            if (errorData && typeof errorData === "string") {
                try {
                    const existing = JSON.parse(errorData);
                    if (existing?.isOfficial) {
                        if (existing?.userId && existing.userId !== user?.id) {
                            setUserTicketOpen(existing?.user?.name || "Outro atendente");
                            setQueueTicketOpen(existing?.queue?.name || "Fila");
                            setOpenAlert(true);
                        } else {
                            toast.warning("Há um ticket oficial aberto para este contato. Encerre o ticket oficial para abrir uma sessão.");
                        }
                        return;
                    }

                    if (existing?.userId && existing.userId !== user?.id) {
                        setUserTicketOpen(existing?.user?.name || "Outro atendente");
                        setQueueTicketOpen(existing?.queue?.name || "Fila");
                        setOpenAlert(true);
                    } else {
                        setOpenAlert(false);
                        const tab = existing.isGroup && existing.channel === 'whatsapp' ? 'group' : 'open';
                        setTabOpenSafe(tab);
                        handleSelectTicket(existing);
                        history.push('/tickets');
                        setTimeout(() => {
                            history.push(`/tickets/${existing.uuid}`);
                        }, 10);
                    }
                } catch (parseErr) {
                    toastError("Já existe um ticket aberto com este contato.");
                }
            } else {
                toastError("Erro ao iniciar sessão.");
            }
        }
    };

    const summarizeLastMessage = (value, len) => {
        if (isNil(value)) {
            return { text: value, tooltip: value, attachmentType: null };
        }

        const raw = `${value}`.trim();
        if (!raw) {
            return { text: raw, tooltip: raw, attachmentType: null };
        }

        const prefixMatch = raw.match(FILE_PREFIX_REGEX);
        const candidate = prefixMatch && prefixMatch[3] ? prefixMatch[3] : raw;
        const sanitizedForExt = candidate.toLowerCase().replace(/\?.*$/, "");
        const attachmentInfo = detectAttachmentSummary(sanitizedForExt);

        if (attachmentInfo) {
            return {
                text: attachmentInfo.label,
                tooltip: candidate,
                attachmentType: attachmentInfo.type,
            };
        }

        if (candidate.length <= len) {
            return { text: candidate, tooltip: candidate, attachmentType: null };
        }

        const clipped = candidate.slice(0, Math.max(1, len - 3));
        return { text: `${clipped}...`, tooltip: candidate, attachmentType: null };
    };

    return (
        <React.Fragment key={ticket.id}>
            {/* Modal de pergunta replicando o design da conversa */}
            <Dialog
                open={confirmAskOpen}
                onClose={handleAskDialogClose}
                aria-labelledby="alert-dialog-title"
                aria-describedby="alert-dialog-description"
            >
                <DialogActions className={classes.botoes}>
                    <Button
                        onClick={() => confirmAskResolver.current && confirmAskResolver.current(false)}
                        style={{ background: "#d1312e", color: "white", fontSize: "14px" }}
                    >
                        <Error style={{ color: "white", fontSize: "30px" }} />
                        {i18n.t("messagesList.header.dialogRatingWithoutFarewellMsg")}
                    </Button>

                    <Button
                        onClick={() => confirmAskResolver.current && confirmAskResolver.current(true)}
                        style={{ background: "#5ebf49", color: "white", fontSize: "14px" }}
                        autoFocus
                    >
                        <VerifiedUser style={{ color: "white", fontSize: "30px" }} />
                        {i18n.t("messagesList.header.dialogRatingCancel")}
                    </Button>
                </DialogActions>
            </Dialog>
            {openAlert && (
                <ShowTicketOpen
                    isOpen={openAlert}
                    handleClose={handleCloseAlert}
                    user={userTicketOpen}
                    queue={queueTicketOpen}
                />
            )}
            {acceptTicketWithouSelectQueueOpen && (
                <AcceptTicketWithouSelectQueue
                    modalOpen={acceptTicketWithouSelectQueueOpen}
                    onClose={() => {
                        if (isMounted.current) {
                            setAcceptTicketWithouSelectQueueOpen(false);
                        }
                    }}
                    ticketId={ticket.id}
                    ticket={ticket}
                />
            )}
            {transferTicketModalOpen && (
                <TransferTicketModalCustom
                    modalOpen={transferTicketModalOpen}
                    onClose={handleCloseTransferTicketModal}
                    ticketid={ticket.id}
                    ticket={ticket}
                />
            )}

                        <ListItem
                button
                dense
                onClick={(e) => {
                    const tagName = e.target.tagName.toLowerCase();
                    const isCheckboxClicked =
                        (tagName === "input" && e.target.type === "checkbox") ||
                        tagName === "svg" ||
                        tagName === "path";

                    if (isCheckboxClicked) return;
                    handleSelectTicket(ticket);
                }}
                selected={ticketId && ticketId === ticket.uuid}
                className={clsx(
                                        classes.ticket,
                                        compact && classes.compactTicket,
                    ticketId === ticket.uuid && classes.selectedListItem,
                    {
                        [classes.pendingTicket]: ticket.status === "pending",
                        [classes.unreadTicket]: Number(ticket.unreadMessages) > 0,
                    }
                )}
            >
                <ListItemAvatar>
                                        <div className={clsx(classes.avatarContainer, compact && classes.compactAvatarContainer)}>
                        <Avatar
                            className={classes.avatar}
                            src={ticket.contact?.urlPicture || ticket.contact?.profilePicUrl || "/nopicture.png"}
                        />
                        {ticket.unreadMessages > 0 && (
                                                        <div className={clsx(classes.badgeContainer, compact && classes.compactBadgeContainer)}>
                                <Badge
                                    badgeContent={ticket.unreadMessages}
                                    classes={{ badge: classes.badgeStyle }}
                                />
                            </div>
                        )}
                        {ticket.channel && (
                            <div className={clsx(classes.connectionIconContainer, compact && classes.compactConnectionIconContainer)}>
                                <ConnectionIcon
                                    className={classes.connectionIcon}
                                    size={compact ? 14 : 16}
                                    connectionType={ticket.channel}
                                />
                            </div>
                        )}
                    </div>
                </ListItemAvatar>

                <ListItemText
                    disableTypography
                    primary={
                        <span className={classes.contactNameWrapper}>
                          <Typography
                            noWrap
                            component="span"
                            variant="body2"
                            style={{
                              display: "flex",
                              alignItems: "center",
                              minWidth: 0
                            }}
                          >
                            {ticket.isGroup && ticket.channel === "whatsapp" && (
                              <GroupIcon
                                fontSize="small"
                                style={{ color: grey[700], marginBottom: "-1px", marginRight: 4 }}
                              />
                            )}
                                                        {(!compact) && ticket.contact?.wallets && ticket.contact.wallets.length > 0 && (
                              <Tooltip 
                                title={`Vinculado à carteira de clientes do usuário: ${ticket.contact.wallets.map(w => w.name).join(', ')}`}
                                arrow
                              >
                                <AccountBox 
                                  fontSize="small"
                                  style={{ color: "#1976d2", marginBottom: "-1px", marginRight: 4 }}
                                />
                              </Tooltip>
                            )}
                            <span
                              style={{
                                flex: 1,
                                minWidth: 0,
                                whiteSpace: "nowrap",
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                                margin: "0 8px 0 0",
                              }}
                            >
                                                            {ticket.contact?.name}
                                                            {isOnlinePresence(ticket) && (
                                                                <span
                                                                    style={{
                                                                        display:'inline-block',
                                                                        width:10,
                                                                        height:10,
                                                                        marginLeft:6,
                                                                        borderRadius:'50%',
                                                                        background:'#34D859',
                                                                        boxShadow:'0 0 0 2px rgba(52,216,89,0.25)'
                                                                    }}
                                                                    title="Online"
                                                                    aria-label="Online"
                                                                />
                                                            )}
                            </span>
                            {Number(ticket.unreadMessages) > 0 && (
                              <>
                                                                {isSameDay(parseISO(ticket.updatedAt), new Date()) ? (
                                                                    <span className={classes.timeChip}>
                                                                        {format(parseISO(ticket.updatedAt), "HH:mm")}
                                                                    </span>
                                                                ) : (
                                                                    <span className={classes.timeChip}>
                                                                        {format(parseISO(ticket.updatedAt), "dd/MM")}
                                                                    </span>
                                                                )}
                              </>
                            )}
                          </Typography>
                        </span>
                      }
                    secondary={
                        <span className={classes.contactNameWrapper}>
                            <Typography
                                className={
                                    Number(ticket.unreadMessages) > 0
                                        ? classes.contactLastMessageUnread
                                        : classes.contactLastMessage
                                }
                                noWrap
                                component="span"
                                variant="body2"
                            >
                                {(() => {
                                    // Unifica lógica: sempre tenta mostrar presença; se não houver, cai na última mensagem / placeholders
                                    const p = getPresenceDisplay(ticket);
                                    try {
                                        const dbgJid = resolveRemoteJid(ticket);
                                        const dbgEntry = getCacheEntry(dbgJid);
                                    } catch {}
                                    if (p) {
                                        // Não sobreescrever a última mensagem para estado 'available'; apenas estados ativos (digitando/gravando)
                                        if (p.type === 'available') {
                                            // cair para render da última mensagem normalmente
                                        } else {
                                        return (
                                            <span style={{ display: 'inline-flex', alignItems: 'center' }}>
                                                {p.type === 'composing' && <CreateIcon style={{ fontSize: 16, marginRight: 4, opacity: 0.85 }} />}
                                                {p.type === 'recording' && <MicIcon style={{ fontSize: 16, marginRight: 4, color: '#d32f2f' }} />}
                                                {p.label}
                                            </span>
                                        );
                                        }
                                    }
                                    if (ticket.lastMessage) {
                                        if (ticket.lastMessage.includes("data:image/png;base64")) return <MarkdownWrapper>Localização</MarkdownWrapper>;
                                        if (ticket.lastMessage.includes("BEGIN:VCARD")) return <MarkdownWrapper>Contato</MarkdownWrapper>;
                                        const summary = summarizeLastMessage(ticket.lastMessage, compact ? 24 : 40);
                                        if (summary.attachmentType) {
                                            const iconStyle = { fontSize: 17, marginRight: 4, opacity: 0.8 };
                                            const icon = summary.attachmentType === "image"
                                                ? <InsertPhotoIcon style={iconStyle} />
                                                : summary.attachmentType === "video"
                                                    ? <MovieCreationIcon style={iconStyle} />
                                                    : <AttachFileIcon style={iconStyle} />;
                                            return (
                                                <span
                                                    title={summary.tooltip || undefined}
                                                    style={{ display: 'inline-flex', alignItems: 'center' }}
                                                >
                                                    {icon}
                                                    <span>{summary.text}</span>
                                                </span>
                                            );
                                        }

                                        return (
                                            <span title={summary.tooltip || undefined}>
                                                <MarkdownWrapper>{summary.text}</MarkdownWrapper>
                                            </span>
                                        );
                                    }
                                    return <br />;
                                })()}
                                {!compact && (
                                                                    <>
                                                                        <span className={classes.secondaryContentSecond}>
                                                                                {ticket?.whatsapp && (
                                                                                        <Badge
                                                                                                className={classes.connectionTag}
                                                                                                style={{
                                                                                                        backgroundColor:
                                                                                                                ticket.channel === "whatsapp"
                                                                                                                        ? "#25D366"
                                                                                                                        : ticket.channel === "facebook"
                                                                                                                                ? "#4267B2"
                                                                                                                                : "#E1306C",
                                                                                                }}
                                                                                        >
                                                                                                {ticket.whatsapp?.name.toUpperCase()}
                                                                                        </Badge>
                                                                                )}
                                                                                <Badge
                                                                                        style={{ backgroundColor: ticket.queue?.color || "#7c7c7c" }}
                                                                                        className={classes.connectionTag}
                                                                                >
                                                                                        {ticket.queueId
                                                                                                ? ticket.queue?.name.toUpperCase()
                                                                                                : ticket.status === "lgpd"
                                                                                                        ? "LGPD"
                                                                                                        : "SEM FILA"}
                                                                                </Badge>
                                                                                {ticket?.user && (
                                                                                        <Badge
                                                                                                style={{ backgroundColor: "#000000" }}
                                                                                                className={classes.connectionTag}
                                                                                        >
                                                                                                {ticket.user?.name.toUpperCase()}
                                                                                        </Badge>
                                                                                )}
                                                                        </span>
                                                                        <span className={classes.secondaryContentSecond}>
                                                                                {ticket.tags?.map((tag) => (
                                                                                        <ContactTag
                                                                                                tag={tag}
                                                                                                key={`ticket-contact-tag-${ticket.id}-${tag.id}`}
                                                                                        />
                                                                                ))}
                                                                        </span>
                                                                    </>
                                                                )}
                            </Typography>
                        </span>
                    }
                />

                                {!compact && (
                                    <ListItemSecondaryAction>
                                        <div className={classes.actionButtonsColumn}>
                        {ticket.status === "pending" && (ticket.queueId === null || ticket.queueId === undefined) && (
                            <ButtonWithSpinner
                                loading={loading}
                                size="small"
                                onClick={handleOpenAcceptTicketWithouSelectQueue}
                                className={clsx(classes.iconButtonBase, classes.acceptButtonStyle)}
                            >
                                <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
                                    <AcceptIcon style={{ fontSize: 25 }} />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {ticket.status === "pending" && ticket.queueId !== null && (
                            <ButtonWithSpinner
                                loading={loading}
                                size="small"
                                onClick={() => handleAcepptTicket(ticket.id)}
                                className={clsx(classes.iconButtonBase, classes.acceptButtonStyle)}
                            >
                                <Tooltip title={i18n.t("ticketsList.buttons.accept")}>
                                    <AcceptIcon style={{ fontSize: 25 }} />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {(ticket.status === "pending" || ticket.status === "open" || ticket.status === "group") && (
                            <ButtonWithSpinner
                                loading={loading}
                                size="small"
                                onClick={(e) => handleOpenTransferModal(e)}
                                className={clsx(classes.iconButtonBase, classes.transferButtonStyle)}
                            >
                                <Tooltip title={i18n.t("ticketsList.buttons.transfer")}>
                                    <TransferIcon style={{ fontSize: 25 }} />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {(ticket.status === "open" || ticket.status === "group") && (
                            <ButtonWithSpinner
                                loading={loading}
                                size="small"
                                onClick={() => handleCloseTicket(ticket.id)}
                                className={clsx(classes.iconButtonBase, classes.closeButtonStyle)}
                            >
                                <Tooltip title={i18n.t("ticketsList.buttons.closed")}>
                                    <CloseIcon style={{ fontSize: 20 }} />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                        {(ticket.status === "pending" || ticket.status === "lgpd") &&
                            (user.userClosePendingTicket === "enabled" || user.profile === "admin") && (
                                <ButtonWithSpinner
                                    loading={loading}
                                    size="small"
                                    onClick={() => handleCloseIgnoreTicket(ticket.id)}
                                    className={clsx(classes.iconButtonBase, classes.ignoreButtonStyle)}
                                >
                                    <Tooltip title={i18n.t("ticketsList.buttons.ignore")}>
                                        <CloseIcon style={{ fontSize: 20 }} />
                                    </Tooltip>
                                </ButtonWithSpinner>
                            )}
                        {ticket.status === "closed" && !manualTicketEnabled && (
                            <ButtonWithSpinner
                                loading={loading}
                                size="small"
                                onClick={
                                    (ticket.queueId === null || ticket.queueId === undefined)
                                        ? handleOpenAcceptTicketWithouSelectQueue
                                        : () => handleAcepptTicket(ticket.id)
                                }
                                className={clsx(classes.iconButtonBase, classes.reopenButtonStyle)}
                            >
                                <Tooltip title={i18n.t("ticketsList.buttons.reopen")}>
                                    <ReopenIcon style={{ fontSize: 25 }} />
                                </Tooltip>
                            </ButtonWithSpinner>
                        )}
                    </div>
                                    </ListItemSecondaryAction>
                                )}
            </ListItem>
        </React.Fragment>
    );
};

export default TicketListItemCustom;

